import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { InputAdornment } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import AnimationPage from '../AnimationPage';
import { setUserData, setUserMenu } from '../../actions/user';
import { authUser } from '../../api/user';
import { getHeadBranchs } from '../../api/point';
import { setCurrentLang } from '../../i18n';
import { errorMessages } from '../../utils/methods';

import {
  BrandIntro,
  BrandTagline,
  BrandTitle,
  BrandTitleRow,
  FieldBlock,
  FieldLabel,
  FieldsGroup,
  FooterDivider,
  FooterLink,
  FooterLinks,
  FormFrame,
  LeftImage,
  LeftImagePane,
  LoginCard,
  PageContainer,
  PasswordToggleIconButton,
  RightPane,
  SaveIdCheckbox,
  SaveIdRow,
  SaveIdText,
  StyledTextField,
  SubmitButton,
} from './LoginStyled';

const STORAGE_KEY_SAVED_USERNAME = 'savedUsername';
const STORAGE_KEY_LANG = 'lang';

// SUPERADMIN이 아니면 로그인 후 첫 헤드 지점을 사용자에 주입.
// 헤드 지점 조회 실패는 치명 오류가 아니므로 로그만 남기고 진행.
const attachHeadBranch = async user => {
  if (user.userRole === 'ROLE_SUPERADMIN') return;
  try {
    const headBranches = await getHeadBranchs(user.token);
    user.headBranch = headBranches[0];
  } catch (err) {
    console.log(err);
  }
};

const persistSavedUsername = (saveId, username) => {
  if (saveId) {
    localStorage.setItem(STORAGE_KEY_SAVED_USERNAME, username);
  } else {
    localStorage.removeItem(STORAGE_KEY_SAVED_USERNAME);
  }
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();

  const { handleSubmit, control } = useForm({
    defaultValues: {
      username: localStorage.getItem(STORAGE_KEY_SAVED_USERNAME) ?? '',
      password: '',
      saveId: !!localStorage.getItem(STORAGE_KEY_SAVED_USERNAME),
    },
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 페이지 진입 시 저장된 언어 한 번만 적용.
  useEffect(() => {
    const lang = localStorage.getItem(STORAGE_KEY_LANG);
    setCurrentLang(lang);
  }, []);

  const togglePasswordVisibility = () => setShowPassword(prev => !prev);

  const handleLogin = async values => {
    setLoading(true);
    try {
      const user = await authUser(values);
      persistSavedUsername(values.saveId, values.username);

      await attachHeadBranch(user);

      // 이전 관리자 전환 데이터 정리 (만료 토큰 방지)
      localStorage.removeItem('pila-original-user');
      localStorage.removeItem('pila-employee-cache');

      const { userMenus, ...rest } = user;
      dispatch(setUserData(rest));
      dispatch(setUserMenu(userMenus));

      // 로그인 직후 1회 공지 팝업 노출 트리거 (MainLayout의 LoginNoticeModal이 소비).
      sessionStorage.setItem('showLoginNotice', '1');

      navigate('/member');
      toast.success(t('Logged in'));
    } catch (err) {
      toast.error(errorMessages(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimationPage>
      <PageContainer id="login-page">
        <LoginCard>
          <LeftImagePane>
            <LeftImage src="/images/screen-login.png" alt="login background" />
          </LeftImagePane>

          <RightPane>
            <FormFrame onSubmit={handleSubmit(handleLogin)}>
              <BrandIntro>
                <BrandTagline>{t('더 쉬운 관리, 더 큰 성공')}</BrandTagline>
                <BrandTitleRow>
                  <BrandTitle bold>BODY</BrandTitle>
                  <BrandTitle>SWITCH</BrandTitle>
                </BrandTitleRow>
              </BrandIntro>

              <FieldsGroup>
                <FieldBlock>
                  <FieldLabel>{t('아이디')}</FieldLabel>
                  <Controller
                    name="username"
                    control={control}
                    render={({ field }) => (
                      <StyledTextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        autoComplete="username"
                        placeholder={t('아이디')}
                      />
                    )}
                  />
                </FieldBlock>

                <FieldBlock>
                  <FieldLabel>{t('비밀번호')}</FieldLabel>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <StyledTextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder={t('비밀번호')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <PasswordToggleIconButton onClick={togglePasswordVisibility} edge="end">
                                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                              </PasswordToggleIconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </FieldBlock>
              </FieldsGroup>

              <SaveIdRow>
                <Controller
                  name="saveId"
                  control={control}
                  render={({ field }) => <SaveIdCheckbox {...field} checked={field.value} />}
                />
                <SaveIdText>{t('아이디 저장')}</SaveIdText>
              </SaveIdRow>

              <SubmitButton id="login-btn" type="submit" size="large" variant="outlined" fullWidth disabled={loading}>
                {t('로그인')}
              </SubmitButton>

              <FooterLinks>
                <FooterLink onClick={() => navigate('/username')}>{t('아이디 찾기')}</FooterLink>
                <FooterDivider>|</FooterDivider>
                <FooterLink onClick={() => navigate('/password')}>{t('비밀번호 찾기')}</FooterLink>
                <FooterDivider>|</FooterDivider>
                <FooterLink onClick={() => navigate('/sign-up')}>{t('회원가입')}</FooterLink>
              </FooterLinks>
            </FormFrame>
          </RightPane>
        </LoginCard>
      </PageContainer>
    </AnimationPage>
  );
};

export default Login;
